/**
 * Core simulation constants.
 *
 * Everything here is shared verbatim between the browser client (for prediction
 * and offline play) and the Durable Object (for authority), so the two stay in
 * lockstep. Anything tuned here changes both at once.
 */

/** Simulation ticks per second. Inputs and snapshots are quantised to this. */
export const TICK_HZ = 30;
/** Seconds per simulation tick. */
export const DT = 1 / TICK_HZ;

/**
 * Snapshots broadcast per second (server -> client).
 *
 * The room sends one every `round(TICK_HZ / SNAPSHOT_HZ)` ticks, so the only
 * rates actually reachable are `TICK_HZ / N`: 30, 15, 10, 7.5… Setting this to
 * 20 does not give 20Hz, it silently gives 15, because the divisor rounds to 2
 * either way. `SNAPSHOT_EVERY` below is the number that decides, and it is
 * exported so the arithmetic is visible rather than hidden in the worker.
 */
export const SNAPSHOT_HZ = 30;
/** Ticks between snapshots. The rate above, made honest. */
export const SNAPSHOT_EVERY = Math.max(1, Math.round(TICK_HZ / SNAPSHOT_HZ));
/** What the server actually broadcasts at, after integer rounding. */
export const SNAPSHOT_HZ_ACTUAL = TICK_HZ / SNAPSHOT_EVERY;

/** Player collision half-extent, in tiles. Slightly under half a tile. */
export const PLAYER_RADIUS = 0.34;

/** Base walk speed in tiles/second, before speed pickups. */
export const BASE_SPEED = 3.6;
/** Additional tiles/second per speed level. */
export const SPEED_STEP = 0.55;
export const MAX_SPEED_LEVEL = 5;
/** Multiplier applied to lane auto-alignment relative to forward movement. */
export const ALIGN_FACTOR = 1.6;
/** How far a player may be nudged sideways per tick to round a corner. */
export const CORNER_SLIP_FACTOR = 1.15;

/** Fuse length in ticks (2.5s). */
export const BOMB_FUSE_TICKS = Math.round(2.5 * TICK_HZ);
/** Shortened fuse under the SHORT_FUSE curse. */
export const SHORT_FUSE_TICKS = Math.round(0.9 * TICK_HZ);
/** How long a blast arm stays lethal + visible. */
export const BLAST_TICKS = Math.round(0.52 * TICK_HZ);
/** Delay between a bomb detonating and its neighbours chaining. */
export const CHAIN_DELAY_TICKS = 2;

/** Tiles/second a kicked bomb travels. */
export const KICK_SPEED = 7.5;
/** Tiles a punched bomb flies before landing. */
export const PUNCH_DISTANCE = 3;
/** Flight time of a punched bomb, in ticks. */
export const PUNCH_TICKS = Math.round(0.45 * TICK_HZ);

export const START_BOMBS = 1;
export const START_POWER = 2;
export const MAX_BOMBS = 8;
export const MAX_POWER = 9;
/** Blast radius granted by the FULLFIRE pickup. */
export const FULL_FIRE_POWER = MAX_POWER;

/** Spawn protection after (re)entering the arena. */
export const SPAWN_INVULN_TICKS = Math.round(2.0 * TICK_HZ);
/** Duration of the SHIELD pickup. */
export const SHIELD_TICKS = Math.round(9 * TICK_HZ);
/** Duration of the GHOST pickup (walk through soft blocks). */
export const GHOST_TICKS = Math.round(8 * TICK_HZ);
/** Duration of any curse inflicted by a SKULL. */
export const CURSE_TICKS = Math.round(12 * TICK_HZ);
/** Delay before a player with lives remaining respawns. */
export const RESPAWN_TICKS = Math.round(2.5 * TICK_HZ);

/** Default round length before sudden death begins. */
export const DEFAULT_ROUND_SECONDS = 180;
/** Ticks between each falling block during sudden death. */
export const SUDDEN_DEATH_INTERVAL = Math.round(0.28 * TICK_HZ);
/** Grace period after the timer expires before blocks start dropping. */
export const SUDDEN_DEATH_WARN_TICKS = Math.round(2.5 * TICK_HZ);

/** Ticks the match lingers on the results screen before the DO tears down. */
export const POST_MATCH_TICKS = Math.round(8 * TICK_HZ);

export const MAX_PLAYERS = 8;

/** Tile kinds stored in the flat grid array. */
export const Tile = {
  EMPTY: 0,
  HARD: 1,
  SOFT: 2,
} as const;
export type TileValue = (typeof Tile)[keyof typeof Tile];

/** Pickups that can sit on an empty tile. */
export const Item = {
  NONE: 0,
  BOMB: 1,
  FIRE: 2,
  SPEED: 3,
  KICK: 4,
  PUNCH: 5,
  REMOTE: 6,
  PIERCE: 7,
  SHIELD: 8,
  FULLFIRE: 9,
  SKULL: 10,
  HEART: 11,
  GHOST: 12,
} as const;
export type ItemValue = (typeof Item)[keyof typeof Item];

export const ITEM_NAMES: Record<number, string> = {
  [Item.BOMB]: 'Extra Bomb',
  [Item.FIRE]: 'Fire Up',
  [Item.SPEED]: 'Speed Up',
  [Item.KICK]: 'Kick',
  [Item.PUNCH]: 'Power Glove',
  [Item.REMOTE]: 'Detonator',
  [Item.PIERCE]: 'Pierce Bomb',
  [Item.SHIELD]: 'Shield',
  [Item.FULLFIRE]: 'Full Fire',
  [Item.SKULL]: 'Skull',
  [Item.HEART]: 'Extra Life',
  [Item.GHOST]: 'Phase Boots',
};

/**
 * Drop table for destroyed soft blocks. Weights are relative; the sum does not
 * need to be 100. `Item.NONE` is included so most blocks drop nothing.
 */
export const DROP_TABLE: ReadonlyArray<readonly [ItemValue, number]> = [
  [Item.NONE, 100],
  [Item.BOMB, 22],
  [Item.FIRE, 22],
  [Item.SPEED, 13],
  [Item.KICK, 7],
  [Item.PUNCH, 5],
  [Item.REMOTE, 4],
  [Item.PIERCE, 4],
  [Item.SHIELD, 5],
  [Item.FULLFIRE, 3],
  [Item.GHOST, 4],
  [Item.HEART, 2],
  [Item.SKULL, 8],
];

/**
 * How each drop's weight bends with the breaker's standing.
 *
 * The measured problem this exists to fix: with one flat table for everyone,
 * the power gap between *equally skilled* players more than doubles in the
 * first thirty seconds and the eventual winner is already ahead within twelve
 * seconds of a two-minute match. Whoever draws well first breaks more blocks,
 * which draws more items, which breaks more blocks. Nothing in the game closes
 * that loop, so a weaker player is beaten by the economy before the fight.
 *
 * The fix is Mario Kart's, and the distinction matters: a leader is not
 * punished and loses nothing they have earned. They simply stop drawing the
 * decisive cards. `FULLFIRE`, `REMOTE`, `PIERCE` and `HEART` become rare at
 * the front and common at the back, while `BOMB`, `FIRE` and `SPEED` -- the
 * ordinary run of play -- stay almost flat. `NONE` rises at the front, which
 * is where most of the effect comes from: the player breaking the most blocks
 * is the one diluting the pool.
 *
 * `SKULL` is deliberately flat. Making the leader eat curses would be
 * punishing them for being ahead, which is the thing this is not.
 *
 * Every pair averages 1.0, so a field that is level rolls exactly the table
 * above. That is not a nicety -- it is what lets a competitive ruleset pin
 * this off and reproduce today's balance precisely.
 *
 *   [at the front, at the back]
 */
export const CATCHUP_BIAS: Record<number, readonly [number, number]> = {
  [Item.NONE]: [1.4, 0.6],
  // Weak predictors. Bending these was the first version of this table and it
  // was aimed at the wrong items: range decides 36% of matches and bomb count
  // 32%, against a 25% baseline. Near flat, because handing them out changes
  // little either way.
  [Item.BOMB]: [1.05, 0.95],
  [Item.FIRE]: [1.05, 0.95],
  [Item.FULLFIRE]: [0.7, 1.3],
  // The decisive ones, measured: speed at 45% and the gear flags at 46%. The
  // first table left SPEED perfectly flat and barely bent KICK and PUNCH,
  // which is to say it withheld nothing that mattered from a leader and
  // offered nothing that mattered to a trailer.
  [Item.SPEED]: [0.4, 1.6],
  [Item.KICK]: [0.4, 1.6],
  [Item.PUNCH]: [0.4, 1.6],
  [Item.REMOTE]: [0.35, 1.65],
  [Item.PIERCE]: [0.35, 1.65],
  [Item.GHOST]: [0.4, 1.6],
  [Item.SHIELD]: [0.45, 1.55],
  // A life is the only drop that touches the loop that actually compounds.
  [Item.HEART]: [0.3, 1.7],
  [Item.SKULL]: [1.0, 1.0],
};

/**
 * What a supply drop can contain.
 *
 * The ordinary table with `NONE` and `SKULL` removed. Nothing is worth running
 * into the open for if it might be nothing, and a curse in a contested pile is
 * a trap rather than an event -- the drop is meant to be an opportunity, and
 * the one rule every mechanic in this game has kept is that it never takes
 * something away.
 *
 * Weights are otherwise the shipped ones, so the drop is the same economy
 * arriving somewhere else rather than a second, different one.
 */
export const SUPPLY_TABLE: ReadonlyArray<readonly [ItemValue, number]> = DROP_TABLE.filter(
  ([item]) => item !== Item.NONE && item !== Item.SKULL,
);

/**
 * When a supply drop lands, as a fraction of the round still to run.
 *
 * Twice, and not near either end: the first is late enough that the blocks are
 * mostly gone and the economy has stopped producing, which is the moment the
 * probe found a trailing player has nothing left to draw from; the second is
 * late enough to still be worth contesting and early enough that winning it
 * means something. A drop in the last ten seconds would be a coin toss with
 * the match on it.
 */
export const SUPPLY_AT = [0.55, 0.25] as const;

/** How many crates a drop scatters. */
export const SUPPLY_COUNT = 6;

/** Warning before it lands, so it can be contested rather than stumbled upon. */
export const SUPPLY_WARN_SECONDS = 3;

/**
 * How far a crate must stay from a living player, in tiles.
 *
 * A block appearing in the square somebody was about to escape into would kill
 * them, and every mechanic here is meant to be an opportunity rather than a
 * tax. One tile of clearance is enough: nothing lands on you or beside you.
 */
export const SUPPLY_CLEARANCE = 1;

/**
 * The power difference, front to back, over which the bias runs its full span.
 *
 * Measured rather than picked: the gap between equals plateaus near 6 power
 * points, so a player 4 above the field average is as far ahead as this game
 * gets and should sit at the front of the table.
 */
export const CATCHUP_RANGE = 8;

/**
 * How far behind the field a player must be, on respawn, to be handed an
 * Overload: one bomb at full range that pierces.
 *
 * This exists because the drop table alone could not fix the problem it was
 * aimed at. Weighting the drops narrowed the gap between equals by about a
 * tenth and left the headline untouched -- the winner is still ahead inside
 * the first twelve seconds -- and the reason is that there is nothing left to
 * hand out. By the end of a match 47 of 48 soft blocks are gone and the whole
 * economy is about eighteen pickups. A player who falls behind cannot be
 * helped by the drop table, because the drops are over.
 *
 * So this is power from a source that is not block-breaking, which is the one
 * thing the measurement said was missing. It is deliberately discrete rather
 * than a trickle: a trickle would close the gap more smoothly and nobody would
 * ever see it happen, and a comeback that cannot be seen is not a comeback,
 * it is a handicap. Everyone is told when it is granted and when it is spent,
 * so it can be played around.
 *
 * Two power points is roughly one Fire Up plus a little -- enough that the
 * player who drew badly qualifies and the player who merely lost a fight does
 * not.
 */
export const CHARGE_DEFICIT = 2;

/**
 * How often a player is checked for being behind, in seconds.
 *
 * Granting only on respawn was the first attempt and it was measured firing
 * 0.8 to 1.4 times a match -- spent almost every time it was granted, so the
 * mechanic worked, it simply never happened. It cannot: first blood lands
 * around twenty-eight seconds and a match holds two to four deaths, while the
 * gap that decides the match has already opened by second twelve.
 *
 * A comeback mechanic keyed to dying cannot reach a player who is losing
 * without dying, and that is the player the numbers were complaining about.
 * So it also checks on a clock, starting inside the window where the gap
 * actually forms. Still capped at one unspent charge, so a player who hoards
 * it gains nothing by waiting.
 */
export const CHARGE_CHECK_SECONDS = 12;

/**
 * Extra respawn protection per death already taken, and its ceiling.
 *
 * MEASURED AS INERT IN BOT MATCHES, and kept deliberately. A four-player bot
 * match produces about four deaths in total -- roughly one each -- so `farmed`
 * is almost always zero and this never fires. Turning the constants to zero
 * reproduced the attribution table to the digit, which is the proof. Farming
 * the same player repeatedly is not what bots do; it may well be what a strong
 * human does to a new one, and that is the case this is here for. Unmeasured
 * rather than disproved.
 *
 * Kill upkeep, in the sense the anti-snowball literature means it: the same
 * advantage is worth progressively less each time it is pressed. The loop the
 * attribution probe found is kill to kill -- leading on kills at thirty
 * seconds predicts the winner at 57% against a 25% baseline, more than any
 * item -- and the cheapest place to attack a loop is where it repeats. A
 * player being farmed comes back harder to farm.
 *
 * It rides on `deaths`, which is already in player state and already on the
 * wire, so this adds no field to synchronise and cannot drift between the
 * browser's prediction and the room's authority.
 *
 * Capped, because the point is to interrupt farming rather than to make a
 * losing player invincible: four extra deaths is the ceiling, at which point
 * respawn protection has doubled from two seconds to four.
 */
export const FARM_INVULN_PER_DEATH = 0.25;
export const FARM_INVULN_MAX_DEATHS = 4;

/**
 * How much a kill is worth against someone who keeps dying.
 *
 * The other half of upkeep. A leader who finds a weak player and farms them is
 * running the loop at its purest, and the scoreboard should stop paying for
 * it. Floors at a third rather than zero: the kill still happened, and a
 * mechanic that made repeat kills worthless would just move the farming to
 * whoever was second weakest.
 */
export const FARM_SCORE_DECAY = 0.2;
export const FARM_SCORE_FLOOR = 0.34;

/**
 * Grace after taking a non-fatal hit, when hit points are in play.
 *
 * Without it a blast lingers over a tile for `BLAST_TICKS` and would take
 * every hit point in a third of a second, which is one-hit death wearing a
 * health bar. Short enough that standing in fire is still fatal, long enough
 * that one bomb costs one hit.
 */
export const HURT_INVULN_TICKS = Math.round(0.9 * TICK_HZ);

/**
 * Full health, as a number rather than a count of hits.
 *
 * Hit points started as an integer count and that is exactly why nothing could
 * scale: three hits is three hits, and a mechanic that wants to make a player
 * ten per cent tougher has nowhere to put the ten per cent. Health is a
 * percentage now, `hitPoints` sets what one clean hit costs, and resistance
 * and lethality become multipliers on that instead of special cases.
 */
export const MAX_HEALTH = 100;

/**
 * Resolve: how far behind a player is, continuously, from 0 to 1.
 *
 * Every comeback mechanic in this project so far has been keyed to death, and
 * every one of them measured as inert for the same reason -- players die about
 * once each per match, so a death-triggered mechanic fires about once each per
 * match. Resolve is checked while the game runs and applies to every exchange,
 * which is the only shape not yet tried.
 *
 * It is Smash's Rage with its one documented mistake corrected. Rage resets on
 * death, so killing a player strips the comeback tool they had earned at
 * exactly the moment they needed it, and the community reads it as punishing
 * the loser. Resolve is a function of standing, so a death that leaves a
 * player further behind *raises* it.
 *
 * The caps are deliberately modest, for the reason Smash's are: at 1.1x nobody
 * feels robbed, and a comeback mechanic that visibly hands someone the game is
 * worse than none. Resistance and lethality are separate because they are
 * different promises -- one lets you survive a mistake, the other lets you
 * punish one.
 */
export const RESOLVE_RANGE = 4;
export const RESOLVE_RESIST_MAX = 0.3;
export const RESOLVE_LETHAL_MAX = 0.3;

/**
 * What the far tip of a blast does, relative to standing on the bomb.
 *
 * Uniform damage was the first version of health and it wasted the system:
 * every tile of every blast cost exactly the same, so a percentage was only
 * ever a count of hits wearing a bigger number. Falloff is what a percentage
 * is *for*.
 *
 * It also repairs a measured oddity. Range came out as the weakest predictor
 * of the winner -- 1.0 to 1.5x against chance, effectively noise -- which is
 * strange for the most visible upgrade in the game. Uniform damage is why:
 * extra range only ever bought more tiles, never a better hit, so a huge bomb
 * and a small one were identical to anyone caught by either. Now the core
 * hurts and the fringe stings, which makes range a positioning question for
 * both players instead of a binary.
 */
export const BLAST_FALLOFF_MIN = 0.55;

/**
 * What a player is currently carrying, as one number.
 *
 * Shared with `tools/balance-probe.ts` on purpose: the thing the drop table
 * reacts to and the thing the probe reports have to be the same measure, or
 * the experiment is not measuring the mechanism.
 *
 * Weighted by what decides a fight rather than by item count -- range and bomb
 * count cut the board and threaten two tiles at once, speed is what escapes
 * your own blast, and the gear flags are advantages a player either has or
 * does not. Lives are not in it: this is about material, and a player on their
 * last life with a full loadout is not the one who needs help.
 */
export function materialPower(p: {
  power: number;
  maxBombs: number;
  speedLevel: number;
  kick: boolean;
  punch: boolean;
  remote: boolean;
  pierce: boolean;
}): number {
  return (
    p.power * 1.0 +
    p.maxBombs * 1.2 +
    p.speedLevel * 0.8 +
    (p.kick ? 1 : 0) +
    (p.punch ? 1 : 0) +
    (p.remote ? 1.5 : 0) +
    (p.pierce ? 1.5 : 0)
  );
}

/** Temporary afflictions inflicted by the SKULL pickup. */
export const Curse = {
  NONE: 0,
  SLOW: 1,
  HYPER: 2,
  TINY_FLAME: 3,
  NO_BOMB: 4,
  AUTO_BOMB: 5,
  REVERSE: 6,
  SHORT_FUSE: 7,
} as const;
export type CurseValue = (typeof Curse)[keyof typeof Curse];

export const CURSE_NAMES: Record<number, string> = {
  [Curse.SLOW]: 'Molasses',
  [Curse.HYPER]: 'Hyperspeed',
  [Curse.TINY_FLAME]: 'Damp Fuse',
  [Curse.NO_BOMB]: 'Bomb Lock',
  [Curse.AUTO_BOMB]: 'Itchy Trigger',
  [Curse.REVERSE]: 'Mirror Mind',
  [Curse.SHORT_FUSE]: 'Hair Trigger',
};

export const CURSE_POOL: readonly CurseValue[] = [
  Curse.SLOW,
  Curse.HYPER,
  Curse.TINY_FLAME,
  Curse.NO_BOMB,
  Curse.AUTO_BOMB,
  Curse.REVERSE,
  Curse.SHORT_FUSE,
];

/** Player slot colours, reused by the renderer, HUD and minimap. */
export const SLOT_COLORS = [
  0xffffff, // white
  0x111318, // black
  0xff3b56, // red
  0x2e7dff, // blue
  0x2ee06a, // green
  0xffc531, // yellow
  0xb44cff, // purple
  0x00d9d5, // teal
] as const;

/** Stable gameplay slots; each identity has its own authored model and rig. */
export const BOMBERS = [
  {
    "name": "Moonboot",
    "bio": "Orbital courier. Every landing is a little too loud."
  },
  {
    "name": "Soot",
    "bio": "Furnace tender. Keeps the embers and the last word."
  },
  {
    "name": "Ninjabot",
    "bio": "Original issue. One quiet step, one very loud exit."
  },
  {
    "name": "Bulkhead",
    "bio": "Deep-sea salvage. Pressure is somebody else’s problem."
  },
  {
    "name": "Sprig",
    "bio": "Rubble gardener. Finds a place to grow in every crater."
  },
  {
    "name": "Zipcap",
    "bio": "Express delivery. Brakes were an optional extra."
  },
  {
    "name": "Jinxbox",
    "bio": "Pocket oracle. Predicted the blast. Forgot to move."
  },
  {
    "name": "Dialtone",
    "bio": "Pirate radio. Announces trouble just before it arrives."
  }
] as const;

export const SLOT_NAMES = BOMBERS.map((b) => b.name);

/** Facing directions, indexed to match `DIR_VECTORS`. */
export const Dir = { DOWN: 0, LEFT: 1, RIGHT: 2, UP: 3 } as const;
export type DirValue = (typeof Dir)[keyof typeof Dir];

export const DIR_VECTORS: ReadonlyArray<readonly [number, number]> = [
  [0, 1], // DOWN  (+y)
  [-1, 0], // LEFT
  [1, 0], // RIGHT
  [0, -1], // UP
];

/* -------------------------------------------------------------------------- */
/* Creatures                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * How close a creature has to be to count as touching you, centre to centre.
 *
 * Under `2 * PLAYER_RADIUS` on purpose. Creatures collide with the world at
 * exactly a player's size -- which is what stops you slipping past one in a
 * corridor, and a contact-damage enemy blocking a corridor is the mechanic
 * rather than a bug -- but a hit registered the instant two collision boxes
 * graze reads as unfair. The tighter number means contact is something the
 * player can see coming.
 */
export const ENEMY_TOUCH_RANGE = 0.52;

/**
 * Grace after a creature has been hit, so one bomb costs one hit point.
 *
 * Same reasoning as `HURT_INVULN_TICKS` and the same length: a blast sits on
 * its tile for `BLAST_TICKS`, so without this every multi-hit creature dies to
 * a single bomb and its hit points are decoration.
 */
export const ENEMY_HURT_TICKS = Math.round(0.9 * TICK_HZ);

/** How far a stalker notices you from, in tiles. */
export const STALKER_SIGHT = 6;

/**
 * How far from a player a creature may be placed when the room opens.
 *
 * Manhattan tiles. Room one starts you next to a drifter otherwise, and a hit
 * taken before the countdown has finished is not difficulty.
 */
export const ENEMY_SPAWN_CLEARANCE = 5;
